// A tiny, framework-free notification bus.
//
// Why it exists: the app deliberately has no toast library (front/CLAUDE.md —
// "Never use toast, use non-blocking notifications instead"), and until now
// every non-blocking message was rendered inline by the page that produced it.
// Permission denials break that assumption: they are raised by the API client
// middleware (src/api/client.ts), which runs *outside* React and has no page to
// render into. So we keep a minimal store here and render it once, next to the
// auth-error banner, in routes/__root.tsx.
//
// Messages are stored as i18n *keys* (plus interpolation values), never as
// resolved strings: the producer is outside React and cannot call `t()`, and a
// language switch must re-label a notification that is still on screen.

export type NotificationVariant = 'default' | 'destructive'

export interface AppNotification {
    /** Unique per occurrence. */
    id: string
    /**
     * Stable identity of the *kind* of notification (e.g. `permission_denied`).
     * A new notification replaces any live one carrying the same key, so a page
     * firing five denied requests shows one banner, not five.
     */
    key: string
    variant: NotificationVariant
    /** i18n key for the headline, resolved by the renderer. */
    titleKey: string
    /** Optional i18n key for the second line. */
    descriptionKey?: string
    /** Interpolation values passed to `t()` for both keys. */
    values?: Record<string, string>
    /** Auto-dismiss delay in ms. `0` keeps it until dismissed. */
    timeoutMs: number
}

type Listener = () => void

let notifications: AppNotification[] = []
const listeners = new Set<Listener>()
const timers = new Map<string, ReturnType<typeof setTimeout>>()

const emit = () => {
    for (const listener of listeners) listener()
}

const clearTimer = (id: string) => {
    const timer = timers.get(id)
    if (timer) {
        clearTimeout(timer)
        timers.delete(id)
    }
}

export function subscribeToNotifications(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

export function getNotifications(): AppNotification[] {
    return notifications
}

export function dismissNotification(id: string) {
    clearTimer(id)
    const next = notifications.filter((n) => n.id !== id)
    if (next.length === notifications.length) return
    notifications = next
    emit()
}

export function notify(
    input: Omit<AppNotification, 'id' | 'timeoutMs'> & { timeoutMs?: number }
): string {
    // Collapse by `key`: repeated denials of the same kind must not stack.
    for (const existing of notifications) {
        if (existing.key === input.key) clearTimer(existing.id)
    }
    const id = `${input.key}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const notification: AppNotification = {
        id,
        timeoutMs: input.timeoutMs ?? 8000,
        variant: input.variant,
        key: input.key,
        titleKey: input.titleKey,
        descriptionKey: input.descriptionKey,
        values: input.values,
    }
    notifications = [
        ...notifications.filter((n) => n.key !== input.key),
        notification,
    ]
    emit()

    if (notification.timeoutMs > 0) {
        timers.set(
            id,
            setTimeout(() => dismissNotification(id), notification.timeoutMs)
        )
    }
    return id
}

/**
 * The canonical "you are not allowed to do that" notification. Raised from
 * exactly two places — the API client's 403 handler and the org route guard —
 * so the wording never diverges and the raw backend error string is never
 * shown to a user.
 */
export const PERMISSION_DENIED_KEY = 'permission_denied'

export function notifyPermissionDenied(permission?: string) {
    notify({
        key: PERMISSION_DENIED_KEY,
        variant: 'destructive',
        titleKey: 'roles:permission_denied.title',
        descriptionKey: permission
            ? 'roles:permission_denied.description_with_permission'
            : 'roles:permission_denied.description',
        values: permission ? { permission } : undefined,
    })
}

/** Test-only helper: drops every live notification and its timer. */
export function resetNotifications() {
    for (const id of timers.keys()) clearTimer(id)
    notifications = []
    emit()
}
